import type { CustomLocation } from "@/utils/location";

type PendingState = {
  activities?: string[];
  area?: string;
  /** Custom location from Google Places or geolocation */
  customLocation?: CustomLocation;
};

/**
 * Singleton store for managing pending search state before it's applied to URL
 */
class PendingSearchStore {
  private state: PendingState = {};
  private listeners = new Set<() => void>();

  /**
   * Same object until a write. `useSyncExternalStore` treats a new snapshot as
   * a change, so a fresh object on every read would re-render forever.
   */
  get = (): PendingState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  setActivities(activities: string[] | null | undefined) {
    // null/undefined means "no change requested" - remove from pending state
    if (activities === null || activities === undefined) {
      const { activities: _, ...rest } = this.state;
      this.state = rest;
    } else {
      this.state = { ...this.state, activities };
    }
    this.notify();
  }

  setArea(area: string | null | undefined) {
    if (!area || area.trim() === "") {
      const { area: _, ...rest } = this.state;
      this.state = rest;
    } else {
      // Clear custom location when setting a predefined area
      const { customLocation: _, ...rest } = this.state;
      this.state = { ...rest, area };
    }
    this.notify();
  }

  setCustomLocation(location: CustomLocation | null | undefined) {
    if (!location) {
      const { customLocation: _, ...rest } = this.state;
      this.state = rest;
    } else {
      // Clear predefined area when setting a custom location
      const { area: _, ...rest } = this.state;
      this.state = { ...rest, customLocation: location };
    }
    this.notify();
  }

  hasPendingChanges(): boolean {
    return (
      this.state.activities !== undefined ||
      this.state.area !== undefined ||
      this.state.customLocation !== undefined
    );
  }

  clear() {
    this.state = {};
    this.notify();
  }
}

const pendingSearch = new PendingSearchStore();
export default pendingSearch;
